import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, CheckCircle, Clock, XCircle } from "@phosphor-icons/react";
import { http } from "@/lib/api";
import SeoMeta from "@/components/SeoMeta";

export function FarmersOrderSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [order, setOrder] = useState(null);
  const [missing, setMissing] = useState(false);
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) { setMissing(true); return; }
    let alive = true;
    const poll = async () => {
      try {
        const { data } = await http.get(`/farmers/shop/orders/status/${sessionId}`);
        if (!alive) return;
        setOrder(data);
        if (data.payment_status !== "paid" && tries.current < 15) {
          tries.current += 1;
          setTimeout(poll, 2000);
        }
      } catch {
        if (alive) setMissing(true);
      }
    };
    poll();
    return () => { alive = false; };
  }, [sessionId]);

  return (
    <main className="min-h-screen bg-white text-[#14532D] pt-32 md:pt-36 pb-24" data-testid="order-success-page">
      <SeoMeta title="Order confirmation — Farmers Pulse" description="Your farm order payment status." />
      <div className="max-w-[560px] mx-auto px-5 text-center">
        {missing ? (
          <>
            <XCircle size={48} weight="light" className="mx-auto text-[#B45309]" />
            <h1 className="mt-4 font-serif-display text-3xl" data-testid="order-missing">We couldn't find that order</h1>
            <p className="mt-2 text-sm text-[#4B5563]">If you just paid, wait a moment and refresh — confirmations can take a few seconds.</p>
          </>
        ) : !order ? (
          <>
            <Clock size={48} weight="light" className="mx-auto text-[#166534] animate-pulse" />
            <h1 className="mt-4 font-serif-display text-3xl">Confirming your payment…</h1>
          </>
        ) : order.payment_status === "paid" ? (
          <>
            <CheckCircle size={56} weight="fill" className="mx-auto text-[#166534]" />
            <h1 className="mt-4 font-serif-display text-3xl text-[#166534]" data-testid="order-paid">Thank you — order confirmed!</h1>
            <div className="mt-5 rounded-2xl border border-[#166534]/15 bg-[#F0FDF4] p-5 text-left" data-testid="order-summary">
              <div className="font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#3F6212]">Order {order.order_id}</div>
              <div className="mt-2 text-lg font-medium">{order.product_name} × {order.quantity}</div>
              <div className="mt-1 text-sm text-[#4B5563]">Paid {order.amount?.toFixed(2)} {order.currency}</div>
              <p className="mt-3 text-sm text-[#4B5563]">The farmer has been notified and will prepare your order. Keep this page as your receipt.</p>
            </div>
          </>
        ) : (
          <>
            <Clock size={48} weight="light" className="mx-auto text-[#B45309]" />
            <h1 className="mt-4 font-serif-display text-3xl" data-testid="order-pending">Payment is processing</h1>
            <p className="mt-2 text-sm text-[#4B5563]">We're still waiting for confirmation from the payment provider. This page refreshes itself.</p>
          </>
        )}
        {order?.return_path && (
          <Link to={order.return_path} data-testid="order-back-to-farm"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#166534] px-6 py-3 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-white hover:bg-[#14532D]">
            <ArrowLeft size={13} weight="bold" /> Back to the farm
          </Link>
        )}
      </div>
    </main>
  );
}

export function FarmersOrderCancel() {
  return (
    <main className="min-h-screen bg-white text-[#14532D] pt-32 md:pt-36 pb-24" data-testid="order-cancel-page">
      <SeoMeta title="Checkout cancelled — Farmers Pulse" description="Your checkout was cancelled — nothing was charged." />
      <div className="max-w-[560px] mx-auto px-5 text-center">
        <XCircle size={48} weight="light" className="mx-auto text-black/30" />
        <h1 className="mt-4 font-serif-display text-3xl">Checkout cancelled</h1>
        <p className="mt-2 text-sm text-[#4B5563]">Nothing was charged. You can return to the farm and try again any time.</p>
        <Link to="/farmers" data-testid="order-cancel-back"
          className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#166534] px-6 py-3 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-white hover:bg-[#14532D]">
          <ArrowLeft size={13} weight="bold" /> Farmers Pulse
        </Link>
      </div>
    </main>
  );
}
